import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Megaphone, X, ArrowRight } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

/**
 * One dismissible strip at the top of the dashboard — the quietest possible
 * nudge. No modal, no takeover. Dismissal is remembered per user, so it
 * never nags, and only one banner runs at a time.
 */
export const AnnouncementBanner = () => {
  const { user } = useAuth();
  const [ann, setAnn] = useState(null);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    api.get('/announcements/banner')
      .then((r) => { if (alive && r.data?.id) setAnn(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [user]);

  if (!ann) return null;

  const dismiss = () => {
    setAnn(null);
    api.post(`/announcements/${ann.id}/dismiss`).catch(() => {});
  };

  const isExternal = /^https?:\/\//i.test(ann.cta_url || '');

  return (
    <div
      className='mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2.5'
      data-testid='announcement-banner'
    >
      <Megaphone className='h-4 w-4 shrink-0 text-primary' />
      <span className='min-w-0 flex-1 text-sm'>
        <strong className='font-semibold'>{ann.title}</strong>
        {ann.banner_text ? <span className='text-muted-foreground'> — {ann.banner_text}</span> : null}
      </span>
      {isExternal ? (
        <a
          href={ann.cta_url}
          className='inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline'
          data-testid='announcement-banner-cta'
        >
          {ann.cta_label} <ArrowRight className='h-3.5 w-3.5' />
        </a>
      ) : (
        <Link
          to={ann.cta_url || '/features'}
          className='inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline'
          data-testid='announcement-banner-cta'
        >
          {ann.cta_label} <ArrowRight className='h-3.5 w-3.5' />
        </Link>
      )}
      <button
        type='button'
        onClick={dismiss}
        className='rounded p-1 text-muted-foreground hover:text-foreground'
        aria-label='Dismiss announcement'
        data-testid='announcement-banner-dismiss'
      >
        <X className='h-4 w-4' />
      </button>
    </div>
  );
};

export default AnnouncementBanner;
